import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { ticketService } from "../../../services/api/ticketService";
import { toast } from "react-toastify";
import { formatDate } from "../../../utils/DateFormat";
import Loading from "../components/Loading";
import CustomSelect from "../components/CustomSelect";
import CustomInput from "../components/CustomInput";

const EditTicket = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const handleInputChange = (field, value) => {
    const updatedData = { ...data, [field]: value };
    setData(updatedData);
  };
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ticketService.get_by_id(id, token);
      if (response.isAnswered) {
        toast.error("Ticket đã đã được trả lời, bạn không thể chỉnh sửa.");
        navigate("/quan-ly-khach-hang/ticket/" + id);
        return;
      }
      const formattedData = {
        name: response.name,
        phone: response.phoneNumber,
        email: response.email,
        date: formatDate(response.createdAt) || "N/A",
        content: response.message || "",
        answer: response.adminResponse || "",
        isAnswered: response.isAnswered,
        status:
          response.status === "pending"
            ? {
                value: "pending",
                label: "Chờ phản hồi",
              }
            : response.status === "in-progress"
            ? {
                value: "in-progress",
                label: "Đang xử lý",
              }
            : {
                value: "resolved",
                label: "Đã hoàn thiện",
              },
        statusColor:
          response.status === "pending"
            ? "bg-orange-500"
            : response.status === "in-progress"
            ? "bg-blue-500"
            : "bg-green-500",
      };

      setData(formattedData);
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response
          ? `${error.response?.data?.message}`
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng."
      );
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  const handleEdit = async () => {
    try {
      setLoading(true);
      await ticketService.edit_ticket(id, data.status.value, token);
      if (data.answer.trim() !== "")
        await ticketService.response(id, data.answer, token);
      toast.success("Cập nhật dữ liệu thành công");
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response
          ? `${error.response?.data?.message}`
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng."
      );
      console.error("Error:", error);
    } finally {
      navigate(`/quan-ly-khach-hang/ticket/${id}`);
      setLoading(false);
    }
  };

  if (!data) {
    return <div>Không tìm thấy dữ liệu</div>;
  }

  return (
    <div className="py-6">
      <div className="bg-white rounded-lg p-6 border-b h-full">
        <h1 className="text-center font-sf-semibold text-[#0457C2] text-2xl uppercase border-b p-4">
          Chi tiết vé #{id}
        </h1>
        <div className="grid grid-cols-3 items-center justify-between px-[5vw] py-6 space-y-3">
          <div className="flex items-start truncate whitespace-nowrap gap-6 ">
            <strong>Họ và tên :</strong>
            <span> {data.name}</span>
          </div>
          <div className="flex items-start truncate whitespace-nowrap gap-6">
            <strong>Số điện thoại :</strong>
            <span>{data.phone}</span>
          </div>
          <div className="flex items-start truncate whitespace-nowrap gap-6">
            <strong>Email :</strong>
            <span>{data.email}</span>
          </div>
          <div className="flex items-start truncate whitespace-nowrap gap-6">
            <strong>Ngày tạo ticket :</strong>
            <span>{data.date}</span>
          </div>
          <div className="flex items-center gap-6">
            <strong>Trạng thái :</strong>
            <CustomSelect
              options={[
                { value: "pending", label: "Chờ phản hồi" },
                { value: "in-progress", label: "Đang xử lý" },
                { value: "resolved", label: "Đã hoàn thiện" },
              ]}
              placeholder="Phân quyền"
              width="200px"
              height="30px"
              required={true}
              value={data.status}
              onChange={(option) => handleInputChange("status", option)}
            />
          </div>
        </div>
        <div className="px-[5vw] py-6 space-y-6">
          <div>
            <strong>Nội dung:</strong>
            <p className="p-4 border rounded-lg mt-2 min-h-32">
              {data.content}
            </p>
          </div>
          <div>
            <strong>Trả lời:</strong>
            <CustomInput
              onChange={(value) => {
                handleInputChange("answer", value);
              }}
              height="2rem"
              value={data.answer}
              isTextarea={true}
            />
          </div>
        </div>

        <div className="flex items-center justify-end px-[5vw]">
          <div className="flex gap-4 ">
            <button
              onClick={() => {
                if (
                  window.confirm("Bạn có chắc chắn muốn hủy thao tác không?")
                ) {
                  navigate(`/quan-ly-khach-hang/ticket/${id}`);
                }
              }}
              className="bg-gray-500 hover:bg-gray-600 rounded-xl py-2 px-3 text-white"
            >
              Hủy
            </button>
            <button
              onClick={handleEdit}
              className="bg-green-500 hover:bg-green-600 rounded-xl py-2 px-3 text-white"
            >
              Trả lời khách hàng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTicket;
